// Discount code box in the cart drawer and on the cart page.
// The markup and every rendered value come from snippets/cart-summary.liquid;
// this file only turns a submitted code into a /cart/update.js call and hands
// the response to the theme's own section renderer, which repaints the whole
// panel from server-rendered HTML. No money maths, no discount aggregation and
// no /cart.js polling happen here.
//
// Listeners are delegated on document because both containers
// (.cart-drawer__footer--subtotal, .cart__footer__js-contents) have their
// innerHTML replaced on every cart change.
(function () {
  var FORM_SELECTOR = '.cart-promo-code__form';
  var REMOVE_SELECTOR = '.cart-promo-code__remove';

  // Defined by assets/ga-custom-events.js. Resolved at interaction time, so the
  // load order of the two scripts does not matter.
  function track(eventName, params) {
    if (typeof window.trackCustomEvent === 'function') {
      window.trackCustomEvent(eventName, params);
    }
  }

  function ctaLocation(element) {
    return element.closest('cart-drawer') ? 'drawer' : 'cart_page';
  }

  // Only one of these exists per page: layout/theme.liquid skips the drawer on
  // the cart page.
  function cartRenderer() {
    return document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
  }

  function activeCodes(form) {
    var raw = (form.dataset.activeCodes || '').trim();
    return raw ? raw.split(',') : [];
  }

  // The note is a sibling of the form, not a child of it, and it is replaced
  // along with the rest of the panel on every re-render — always look it up
  // fresh off the document.
  function showNote(message, isError) {
    var note = document.querySelector('.cart-promo-code__note');
    if (!note) return;
    note.textContent = message;
    note.classList.remove('hidden');
    // Drawer: the note lives inside a collapsed <details>; a message the
    // shopper needs to read must open it.
    var details = note.closest('details');
    if (details && !details.open) details.open = true;
    note.classList.toggle('cart-promo-code__note--error', !!isError);
  }

  function hideNote() {
    var note = document.querySelector('.cart-promo-code__note');
    if (note) note.classList.add('hidden');
  }

  // The section ids the theme's own renderer needs. The cart page list names
  // the same section twice, so dedupe before asking Shopify for it.
  function sectionIdsToRender(renderer) {
    var ids = [];
    renderer.getSectionsToRender().forEach(function (section) {
      if (ids.indexOf(section.id) === -1) ids.push(section.id);
    });
    return ids;
  }

  // renderContents() throws on a missing section, so all of them must be there.
  function hasAllSections(cart, ids) {
    return !!cart.sections && ids.every(function (id) {
      return typeof cart.sections[id] === 'string';
    });
  }

  // Replaces the cart's whole discount code set in one call: Shopify only
  // combines codes that are requested together, and decides for itself which of
  // them end up active. Requesting the section render in the same response lets
  // the theme's renderer repaint the cart without a second round trip.
  //
  // Setting the same code set twice is a no-op, so this is safe to re-issue.
  function requestCart(codes, sectionIds) {
    var body = JSON.stringify({
      discount: codes.join(','),
      sections: sectionIds,
      sections_url: window.location.pathname
    });
    return fetch(routes.cart_update_url, Object.assign(fetchConfig(), { body: body }))
      .then(function (response) {
        if (!response.ok) throw new Error('discount update failed');
        return response.json();
      });
  }

  // The rendered panel carries the cart's discount total, so this is a direct
  // check that what is on screen agrees with the cart we were handed.
  function panelMatches(cart) {
    var form = document.querySelector(FORM_SELECTOR);
    if (!form) return true; // empty cart: nothing to paint
    return parseInt(form.dataset.totalDiscount, 10) === (cart.total_discount || 0);
  }

  function paint(renderer, cart, ids) {
    if (!hasAllSections(cart, ids)) throw new Error('missing sections');
    renderer.renderContents(cart);
  }

  // Observed live: a cart mutation can answer 200 with the cart updated but
  // "sections": null, leaving the panel showing the old totals. Re-issuing the
  // same request gets the render — it is the state-changing call that comes
  // back empty, and asking again once the state has settled does not.
  //
  // A standalone GET ?sections=cart-drawer is NOT an alternative: cart-drawer
  // is a static section in the layout rather than one of the template's, so
  // that endpoint answers with the template's own sections instead.
  //
  // Resolves with whichever cart was actually painted, and never rejects — a
  // repaint that fails must not be reported to the shopper as a failed
  // discount, because the cart has already changed either way.
  function repaint(renderer, cart, codes, ids) {
    try {
      paint(renderer, cart, ids);
      if (panelMatches(cart)) return Promise.resolve(cart);
    } catch (error) {
      // fall through to the retry
    }

    return requestCart(codes, ids)
      .then(function (fresh) {
        paint(renderer, fresh, ids);
        if (!panelMatches(fresh)) throw new Error('panel still stale after re-render');
        return fresh;
      })
      .catch(function (error) {
        if (window.console) console.error('[cart-promo-code] repaint failed', error);
        return cart;
      });
  }

  function updateDiscount(codes) {
    var renderer = cartRenderer();
    var sectionIds = renderer ? sectionIdsToRender(renderer) : [];

    return requestCart(codes, sectionIds)
      .then(function (cart) {
        return renderer ? repaint(renderer, cart, codes, sectionIds) : cart;
      })
      .then(function (cart) {
        // The free shipping bar sits outside both re-render targets and
        // refreshes itself on this event.
        document.dispatchEvent(new CustomEvent('afterCartChanged', {
          detail: { source: 'cart-promo-code', cartData: cart }
        }));
        return cart;
      });
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form.matches || !form.matches(FORM_SELECTOR)) return;
    event.preventDefault();

    var input = form.querySelector('.cart-promo-code__input');
    var submit = form.querySelector('.cart-promo-code__submit');
    var code = input.value.trim();
    if (!code || submit.disabled) return;

    var promoCode = code.toUpperCase();
    var cartLocation = ctaLocation(form);
    var codesBefore = activeCodes(form);
    var discountBefore = parseInt(form.dataset.totalDiscount, 10) || 0;
    var pendingMessage = (form.dataset.pendingMessage || '').replace('[code]', code);
    var errorMessage = form.dataset.errorMessage || '';
    var alreadyActive = codesBefore.some(function (title) {
      return title.toUpperCase() === promoCode;
    });

    track('promo_code_submitted', { promo_code: promoCode, cta_location: cartLocation });

    if (alreadyActive) {
      track('promo_code_apply_result', {
        promo_code: promoCode,
        cta_location: cartLocation,
        result: 'already_active',
        discount_delta: 0,
        active_codes_before: codesBefore.length
      });
      showNote(pendingMessage, false);
      return;
    }

    var label = submit.querySelector('span');
    var originalLabel = label.textContent;
    input.disabled = true;
    submit.disabled = true;
    label.textContent = form.dataset.applyingLabel || originalLabel;
    hideNote();

    updateDiscount(codesBefore.concat([code]))
      .then(function (cart) {
        var delta = (cart.total_discount || 0) - discountBefore;
        track('promo_code_apply_result', {
          promo_code: promoCode,
          cta_location: cartLocation,
          result: delta > 0 ? 'applied' : 'no_effect',
          discount_delta: delta / 100,
          active_codes_before: codesBefore.length
        });
        // The panel has been re-rendered, so the field and the note are fresh
        // nodes: a code that worked leaves the field empty, one that changed
        // nothing keeps what was typed next to the explanation.
        if (delta <= 0) {
          var freshInput = document.querySelector('.cart-promo-code__input');
          if (freshInput) freshInput.value = code;
          showNote(pendingMessage, false);
        }
      })
      .catch(function () {
        track('promo_code_apply_result', {
          promo_code: promoCode,
          cta_location: cartLocation,
          result: 'error',
          active_codes_before: codesBefore.length
        });
        showNote(errorMessage, true);
      })
      .finally(function () {
        // No-op once the re-render has replaced these nodes; restores them when
        // the request failed and they are still on screen.
        if (!document.contains(submit)) return;
        input.disabled = false;
        submit.disabled = false;
        label.textContent = originalLabel;
      });
  }

  // Removes one code out of a possibly stacked set by re-requesting the others.
  function onClick(event) {
    if (!event.target || !event.target.closest) return;
    var button = event.target.closest(REMOVE_SELECTOR);
    if (!button || button.disabled) return;
    event.preventDefault();

    var form = document.querySelector(FORM_SELECTOR);
    if (!form) return;

    var title = button.dataset.code || '';
    var promoCode = title.toUpperCase();
    var cartLocation = ctaLocation(button);
    var errorMessage = form.dataset.errorMessage || '';
    var remaining = activeCodes(form).filter(function (code) {
      return code !== title;
    });

    button.disabled = true;

    updateDiscount(remaining)
      .then(function () {
        track('promo_code_removed', {
          promo_code: promoCode,
          cta_location: cartLocation,
          result: 'removed'
        });
      })
      .catch(function () {
        track('promo_code_removed', {
          promo_code: promoCode,
          cta_location: cartLocation,
          result: 'error'
        });
        showNote(errorMessage, true);
      })
      .finally(function () {
        if (document.contains(button)) button.disabled = false;
      });
  }

  document.addEventListener('submit', onSubmit);
  document.addEventListener('click', onClick);
})();
